import {
	ReactElement,
	useCallback,
	useMemo,
	useState
} from "react";
import { deleteCategories, HubItemKind } from "client/Api/Hub/hubItemCategories";
import {
	Dialog,
	FormattedText,
	Toolbar
} from "client/Components";
import {
	AlertManager,
	useAlerts,
	useDataInputDialog,
	useUnmountedCancellation
} from "client/Hooks";
import { isCancelledError } from "client_legacy/ndaw/cancellation";
import { localize } from "client_legacy/ndaw/culture";
import { never } from "client_legacy/ndaw/errors";
import styles from "./DeleteHubItemCategoriesDialog.module.less";

export type DeleteHubItemCategoriesDialogProps = {
	hubItemKind: HubItemKind;
	categoryIds: readonly number[];
	onDeleted(): void;
	onClose(): void;
};

export const DeleteHubItemCategoriesDialog = (props: DeleteHubItemCategoriesDialogProps): ReactElement => {
	const {
		categoryIds,
		hubItemKind,
		onDeleted,
		onClose
	} = props;

	const [isBusy, setIsBusy] = useState<boolean>(false);

	const globalCancellation: AbortSignal = useUnmountedCancellation();
	const alertManager: AlertManager = useAlerts();

	const deleteHandler = useCallback(
		async(): Promise<void> => {
			try {
				setIsBusy(true);
				await deleteCategories(categoryIds, globalCancellation);
				onDeleted();
			} catch (e) {
				if (isCancelledError(e)) {
					return;
				}

				console.error(e);
				alertManager.showError(localize("_GeneralErrorMessage"));
				setIsBusy(false);
			}
		},
		[
			alertManager,
			categoryIds,
			globalCancellation,
			onDeleted
		]
	);

	const isSingleCategory: boolean = categoryIds.length === 1;

	const title = isSingleCategory
		? localize("_AreYouSureYouWantToDeleteThisCategory")
		: localize("_AreYouSureYouWantToDeleteTheseCategories");

	const errorLocalizeKey = useMemo(
		(): LocalizationKey => {
			switch(hubItemKind) {
				case HubItemKind.Announcement:
					return isSingleCategory
						? "_DeleteSingleAnnouncementCategoryInfoText"
						: "_DeleteMultipleAnnouncementCategoryInfoText";
				case HubItemKind.Event:
					return isSingleCategory
						? "_NoteAllEventsWithinThisCategoryWillBeDeletedAsWell"
						: "_NoteAllEventsWithinTheseCategoriesWillBeDeletedAsWell";
				case HubItemKind.KnowledgeItem:
					return isSingleCategory
						? "_NoteAllKnowledgeArticlesWithinThisCategoryWillBeDeletedAsWell"
						: "_NoteAllKnowledgeArticlesWithinTheseCategoriesWillBeDeletedAsWell";
				default:
					never(hubItemKind);
			}
		},
		[
			hubItemKind,
			isSingleCategory
		]
	);

	return (
		<Dialog
			trackingId="delete-hub-item-categories-dialog"
			hasBusyIndicator={isBusy}
		>
			<Dialog.Header
				title={title}
			/>
			<Dialog.Body>
				<span className={styles.deleteCategoryWarningText}>
					<FormattedText
						text={
							localize(errorLocalizeKey)
						}
						thingsToReplace={[
							{ what: "{{Note}}", filler: <b>{localize("_Note")}</b> }
						]}
					/>
				</span>
			</Dialog.Body>
			<Dialog.Footer>
				<Toolbar>
					<Toolbar.Button
						kind="primary"
						text={localize("_Delete")}
						onClick={deleteHandler}
						trackingId="confirm"
					/>
					<Toolbar.Button
						kind="secondary"
						text={localize("_Cancel")}
						onClick={onClose}
						trackingId="cancel"
					/>
				</Toolbar>
			</Dialog.Footer>
		</Dialog>
	);
};
DeleteHubItemCategoriesDialog.displayName = "DeleteHubItemCategoriesDialog";

export type DeleteHubItemCategoriesDialogInitialData = Omit<
	DeleteHubItemCategoriesDialogProps,
	"onClose" | "onDeleted"
>;
export function useDeleteHubItemCategoriesDialog(): [
	element: ReactElement | null,
	open: (props: DeleteHubItemCategoriesDialogInitialData) => Promise<TryResult<void>>
] {
	return useDataInputDialog(
		(props, onClose, onDeleted) => (
			<DeleteHubItemCategoriesDialog
				{...props}
				onClose={onClose}
				onDeleted={onDeleted}
			/>
		)
	);
}